import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import TiptapEditor from "../common/TiptapEditor";
import { X, Upload, Image as ImageIcon } from "lucide-react";
import { newsService } from "../../services/newsService";
import { uploadService } from "../../services/uploadService";
import {
  News,
  CreateNewsRequest,
  UpdateNewsRequest,
} from "../../types/landing";

interface NewsFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  news?: News | null; // If provided, we're editing
}

const NewsFormModal: React.FC<NewsFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  news,
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [content, setContent] = useState("");
  const [tagsInput, setTagsInput] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateNewsRequest>();

  const isEditing = !!news;

  useEffect(() => {
    if (isOpen) {
      if (isEditing && news) {
        // Populate form with existing news data
        setValue("title", news.title);
        setValue("excerpt", news.excerpt || "");
        setValue("category", news.category);
        setTagsInput(news.tags ? news.tags.join(", ") : "");
        setValue("status", news.status);
        setValue("publishDate", news.publishDate.split("T")[0]); // Format for date input
        setValue("author", news.author);
        setValue("imageUrl", news.imageUrl || "");
        setContent(news.content);
      } else {
        // Reset form for new news
        reset();
        setContent("");
        setSelectedImageFile(null);
        setTagsInput("");
      }
    }
  }, [isOpen, isEditing, news, setValue, reset]);

  const handleImageUpload = (file: File) => {
    setSelectedImageFile(file);
    // Show a preview or just indicate the file is selected
    toast.success(
      "Image selected! It will be uploaded when you submit the form."
    );
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit for news images
        toast.error("Image size should be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      handleImageUpload(file);
    }
  };

  const onSubmit = async (data: CreateNewsRequest) => {
    setLoading(true);
    try {
      // Start with existing image URL (for editing) or form data
      let imageUrl = isEditing && news ? news.imageUrl : data.imageUrl;

      // Upload image only if a new file is selected
      if (selectedImageFile) {
        setUploadingImage(true);
        try {
          const uploadResult = await uploadService.uploadFile(
            selectedImageFile,
            "landingpage/news"
          );
          if (uploadResult.success && uploadResult.data) {
            imageUrl = uploadResult.data.url;
          } else {
            throw new Error("Failed to upload image");
          }
        } finally {
          setUploadingImage(false);
        }
      }

      const newsData = {
        ...data,
        imageUrl,
        content,
        publishDate: data.publishDate || new Date().toISOString(),
      };

      if (isEditing && news) {
        await newsService.updateNews(news._id, newsData as UpdateNewsRequest);
        toast.success("News updated successfully!");
      } else {
        await newsService.createNews(newsData);
        toast.success("News created successfully!");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save news. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Center the modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 z-10 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <ImageIcon className="h-6 w-6 text-blue-600" />
              {isEditing ? "Edit News" : "Create News"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                {...register("title", { required: "Title is required" })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.title ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter news title"
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Excerpt
              </label>
              <textarea
                {...register("excerpt")}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief summary of the news article"
              />
            </div>

            {/* Category and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  {...register("category", {
                    required: "Category is required",
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.category ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select category</option>
                  <option value="announcement">Announcement</option>
                  <option value="event">Event</option>
                  <option value="achievement">Achievement</option>
                  <option value="update">Update</option>
                  <option value="general">General</option>
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.category.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  {...register("status")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>

            {/* Author and Publish Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Author *
                </label>
                <input
                  type="text"
                  {...register("author", { required: "Author is required" })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.author ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Author name"
                />
                {errors.author && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.author.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Publish Date
                </label>
                <input
                  type="date"
                  {...register("publishDate")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="tag1, tag2, tag3"
                value={tagsInput}
                onChange={(e) => {
                  setTagsInput(e.target.value);
                  const tags = e.target.value
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter((tag) => tag);
                  setValue("tags", tags);
                }}
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Image
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="image-upload"
                  disabled={uploadingImage}
                />
                <label
                  htmlFor="image-upload"
                  className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 ${
                    uploadingImage ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <Upload className="h-4 w-4" />
                  {uploadingImage ? "Uploading..." : "Choose Image"}
                </label>
                {watch("imageUrl") && (
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <img
                        src={watch("imageUrl")}
                        alt="News preview"
                        className="w-16 h-16 object-cover rounded-md border border-gray-300"
                        onError={(e) => {
                          // Hide broken images
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1">
                        <ImageIcon className="h-3 w-3" />
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {selectedImageFile
                        ? "New image selected"
                        : "Current image"}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content *
              </label>
              <TiptapEditor
                value={content}
                onChange={setContent}
                placeholder="Write your news content here..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading
                  ? "Saving..."
                  : isEditing
                  ? "Update News"
                  : "Create News"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewsFormModal;
